import React from 'react';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { companyInfo } from '../config/seoConfig';
import { 
  FaCode, 
  FaReact, 
  FaNodeJs, 
  FaDatabase, 
  FaAws, 
  FaBriefcase,
  FaGraduationCap,
  FaCertificate,
  FaTrophy,
  FaMapMarkerAlt,
  FaCheckCircle,
  FaMobile,
  FaServer,
  FaCloud,
  FaPalette,
  FaRocket,
  FaDownload,
  FaStar,
  FaLightbulb,
  FaGithub,
  FaLinkedin,
  FaWhatsapp,
  FaEnvelope,
  FaCalendar,
  FaCogs,
  FaCodeBranch,
  FaLinux,
  FaTerminal,
  FaSearch,
  FaChartLine,
  FaSearchPlus,
  FaBolt
} from 'react-icons/fa';
import { 
  SiTypescript, 
  SiPython, 
  SiMongodb, 
  SiPostgresql,
  SiDocker,
  SiKubernetes,
  SiGraphql,
  SiNextdotjs,
  SiJavascript,
  SiRedux,
  SiTailwindcss,
  SiExpress,
  SiNestjs,
  SiGit,
  SiJenkins,
  SiGithubactions,
  SiNginx
} from 'react-icons/si';
import './test.scss';

const Resume: React.FC = () => {
  // SEO constants
  const resumeSeoTitle = "Vishnu Charan - Full Stack Expert Resume | React, Node.js, TypeScript Expert";
  const resumeSeoDescription = "Professional resume of Vishnu Charan, Full Stack Expert with 8+ years of experience in React, Node.js, TypeScript, Python, AWS, and modern web technologies. Available for hire.";
  const resumeSeoKeywords = "vishnu charan resume, full stack developer resume, react developer CV, node.js developer, typescript expert, AI developer, web developer resume, hire developer";
  const resumeSeoUrl = "https://appdeveloperpro.com/resume";

  const personalInfo = {
    name: 'Vishnu Charan',
    title: 'Full Stack Expert',
    location: 'Agra, Uttar Pradesh, India',
    email: 'hello@appdeveloperpro.com',
    phone: '+91 882 636 0188',
    website: 'https://appdeveloperpro.com',
    linkedin: 'https://linkedin.com/in/vishnu-charan',
    github: 'https://github.com/vishnucharan',
    experience: '10+ Years',
    projects: '150+',
    clients: '95%',
    rating: '4.9/5'
  };

  const technicalSkills = [
    {
      category: 'Frontend Development',
      icon: <FaPalette />,
      skills: [
        { name: 'React.js', level: 95, icon: <FaReact />, color: '#61DAFB' },
        { name: 'Next.js', level: 90, icon: <SiNextdotjs />, color: '#000000' },
        { name: 'TypeScript', level: 92, icon: <SiTypescript />, color: '#3178C6' },
        { name: 'JavaScript (ES6+)', level: 95, icon: <SiJavascript />, color: '#F7DF1E' },
        { name: 'Redux/Context API', level: 88, icon: <SiRedux />, color: '#764ABC' },
        { name: 'Tailwind CSS', level: 90, icon: <SiTailwindcss />, color: '#06B6D4' },
      ]
    },
    {
      category: 'Backend Development',
      icon: <FaServer />,
      skills: [
        { name: 'Node.js', level: 93, icon: <FaNodeJs />, color: '#339933' },
        { name: 'Express.js', level: 92, icon: <SiExpress />, color: '#000000' },
        { name: 'NestJS', level: 85, icon: <SiNestjs />, color: '#E0234E' },
        { name: 'Python', level: 87, icon: <SiPython />, color: '#3776AB' },
        { name: 'GraphQL', level: 88, icon: <SiGraphql />, color: '#E10098' },
        { name: 'REST APIs', level: 95, icon: <FaCode />, color: '#7C3AED' },
      ]
    },
    {
      category: 'Database & Cloud',
      icon: <FaCloud />,
      skills: [
        { name: 'MongoDB', level: 92, icon: <SiMongodb />, color: '#47A248' },
        { name: 'PostgreSQL', level: 88, icon: <SiPostgresql />, color: '#4169E1' },
        { name: 'AWS', level: 85, icon: <FaAws />, color: '#FF9900' },
        { name: 'Docker', level: 87, icon: <SiDocker />, color: '#2496ED' },
        { name: 'Kubernetes', level: 80, icon: <SiKubernetes />, color: '#326CE5' },
        { name: 'Git/GitHub', level: 93, icon: <SiGit />, color: '#F05032' },
      ]
    },
    {
      category: 'Mobile Development',
      icon: <FaMobile />,
      skills: [
        { name: 'React Native', level: 88, icon: <FaReact />, color: '#61DAFB' },
        { name: 'Progressive Web Apps', level: 90, icon: <FaMobile />, color: '#5A0FC8' },
        { name: 'Responsive Design', level: 95, icon: <FaPalette />, color: '#EC4899' },
        { name: 'Flutter', level: 82, icon: <FaMobile />, color: '#02569B' },
        { name: 'iOS/Android Native', level: 80, icon: <FaMobile />, color: '#3DDC84' },
        { name: 'Mobile UI/UX', level: 92, icon: <FaPalette />, color: '#FF6B6B' },
      ]
    },
    {
      category: 'DevOps',
      icon: <FaCogs />,
      skills: [
        { name: 'CI/CD Pipelines', level: 85, icon: <FaCodeBranch />, color: '#2088FF' },
        { name: 'Jenkins', level: 82, icon: <SiJenkins />, color: '#D24939' },
        { name: 'GitHub Actions', level: 88, icon: <SiGithubactions />, color: '#2088FF' },
        { name: 'Nginx', level: 83, icon: <SiNginx />, color: '#009639' },
        { name: 'Linux/Ubuntu', level: 87, icon: <FaLinux />, color: '#FCC624' },
        { name: 'Shell Scripting', level: 80, icon: <FaTerminal />, color: '#4EAA25' },
      ]
    },
    {
      category: 'SEO & Marketing',
      icon: <FaSearch />,
      skills: [
        { name: 'On-Page SEO', level: 90, icon: <FaSearch />, color: '#4285F4' },
        { name: 'Technical SEO', level: 88, icon: <FaChartLine />, color: '#34A853' },
        { name: 'Google Analytics', level: 85, icon: <FaChartLine />, color: '#E37400' },
        { name: 'Keyword Research', level: 87, icon: <FaSearchPlus />, color: '#FBBC04' },
        { name: 'Schema Markup', level: 83, icon: <FaCode />, color: '#EA4335' },
        { name: 'Core Web Vitals', level: 90, icon: <FaBolt />, color: '#0F9D58' },
      ]
    },
  ];

  const experience = [
    {
      title: 'Senior Full Stack Developer',
      company: 'App Developer Pro (Freelance)',
      location: 'Remote',
      period: '2020 - Present',
      description: 'Leading full-stack development projects for international clients, specializing in React, Node.js, and cloud solutions.',
      achievements: [
        'Delivered 150+ successful projects with 95% client satisfaction rate',
        'Built scalable web applications serving 500K+ users',
        'Reduced application load time by 60% through performance optimization',
        'Implemented CI/CD pipelines reducing deployment time by 75%',
        'Mentored junior developers and conducted code reviews'
      ],
      technologies: ['React', 'Node.js', 'TypeScript', 'AWS', 'MongoDB', 'Docker']
    },
    {
      title: 'Full Stack Developer',
      company: 'Tech Solutions Ltd.',
      location: 'India',
      period: '2018 - 2020',
      description: 'Developed enterprise-level web applications and REST APIs for various industries including e-commerce and fintech.',
      achievements: [
        'Architected and developed 30+ web applications',
        'Improved API response time by 45% through optimization',
        'Integrated payment gateways processing $2M+ monthly transactions',
        'Implemented secure authentication systems with JWT and OAuth',
        'Collaborated with cross-functional teams in Agile environment'
      ],
      technologies: ['JavaScript', 'Express.js', 'PostgreSQL', 'React', 'Redux']
    },
    {
      title: 'Junior Web Developer',
      company: 'Digital Innovations',
      location: 'India',
      period: '2016 - 2018',
      description: 'Started career developing responsive websites and learning modern web development practices.',
      achievements: [
        'Built 40+ responsive websites using modern frameworks',
        'Achieved 100% mobile responsiveness across all projects',
        'Implemented SEO best practices improving organic traffic by 120%',
        'Collaborated with designers to create pixel-perfect UIs'
      ],
      technologies: ['HTML', 'CSS', 'JavaScript', 'jQuery', 'Bootstrap', 'WordPress']
    },
  ];

  const education = [
    {
      degree: 'Bachelor of Technology in Computer Science',
      institution: 'AKTU University',
      location: 'Uttar Pradesh, India',
      period: '2012 - 2016',
      grade: 'First Class with Distinction (75%)',
      achievements: [
        'Specialized in Web Technologies and Software Engineering',
        'Final year project: Real-time Chat Application using Socket.io',
        'Member of coding club and participated in hackathons'
      ]
    },
  ];

  const certifications = [
    { name: 'AWS Certified Solutions Architect', issuer: 'Amazon Web Services', year: '2023' },
    { name: 'MongoDB Certified Developer', issuer: 'MongoDB University', year: '2022' },
    { name: 'React Advanced Patterns', issuer: 'Udemy', year: '2021' },
    { name: 'Node.js Application Development', issuer: 'Coursera', year: '2020' },
    { name: 'TypeScript Advanced Techniques', issuer: 'Frontend Masters', year: '2021' },
    { name: 'Docker Certified Associate', issuer: 'Docker Inc.', year: '2022' },
  ];

  const achievements = [
    { icon: <FaTrophy />, title: 'Top Rated Freelancer', description: '4.9/5 rating with 50+ reviews on Upwork' },
    { icon: <FaStar />, title: 'Client Choice Award', description: 'Awarded by multiple clients for exceptional work' },
    { icon: <FaCheckCircle />, title: '100% Job Success Score', description: 'Perfect track record on freelance platforms' },
    { icon: <FaLightbulb />, title: 'Innovation Award', description: 'Recognized for creative problem-solving' },
  ];

  const socialLinks = [
    { icon: <FaGithub />, url: personalInfo.github, label: 'GitHub', color: '#181717' },
    { icon: <FaLinkedin />, url: personalInfo.linkedin, label: 'LinkedIn', color: '#0A66C2' },
    { icon: <FaWhatsapp />, url: `https://wa.me/${personalInfo.phone.replace(/\s/g, '')}`, label: 'WhatsApp', color: '#25D366' },
    { icon: <FaEnvelope />, url: `mailto:${personalInfo.email}`, label: 'Email', color: '#EA4335' },
  ];

  const schemaData = {
    "@context": "https://schema.org",
    "@type": "Person",
    "name": personalInfo.name,
    "jobTitle": personalInfo.title,
    "url": personalInfo.website,
    "email": personalInfo.email,
    "telephone": personalInfo.phone,
    "address": {
      "@type": "PostalAddress",
      "addressLocality": "Agra",
      "addressRegion": "Uttar Pradesh",
      "addressCountry": "India"
    },
    "sameAs": [
      personalInfo.linkedin,
      personalInfo.github,
      personalInfo.website
    ],
    "alumniOf": {
      "@type": "Organization",
      "name": "AKTU University"
    },
    "knowsAbout": [
      "React.js", "Node.js", "TypeScript", "JavaScript", "Python",
      "AWS", "MongoDB", "PostgreSQL", "Docker", "Kubernetes",
      "Full Stack Development", "Web Development", "Mobile Development",
      "Cloud Computing", "DevOps"
    ],
    "hasCredential": certifications.map(cert => ({
      "@type": "EducationalOccupationalCredential",
      "credentialCategory": "certificate",
      "name": cert.name,
      "recognizedBy": {
        "@type": "Organization",
        "name": cert.issuer
      }
    }))
  };

  return (
    <>
      <Helmet>
        <title>{resumeSeoTitle}</title>
        <meta name="description" content={resumeSeoDescription} />
        <meta name="keywords" content={resumeSeoKeywords} />
        <meta name="author" content={personalInfo.name} />
        <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
        
        {/* Open Graph */}
        <meta property="og:title" content={resumeSeoTitle} />
        <meta property="og:description" content={resumeSeoDescription} />
        <meta property="og:type" content="profile" />
        <meta property="og:url" content={resumeSeoUrl} />
        <meta property="og:image" content={`${companyInfo.url}/images/vishnu-charan-resume.jpg`} />
        <meta property="profile:first_name" content="Vishnu" />
        <meta property="profile:last_name" content="Charan" />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={resumeSeoTitle} />
        <meta name="twitter:description" content={resumeSeoDescription} />
        <meta name="twitter:image" content={`${companyInfo.url}/images/vishnu-charan-resume.jpg`} />
        
        <link rel="canonical" href={resumeSeoUrl} />
        <script type="application/ld+json">{JSON.stringify(schemaData)}</script>
      </Helmet>

      <div className="resume-container" style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #0F172A 0%, #1E293B 50%, #0F172A 100%)',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Animated Background */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          zIndex: 0,
          overflow: 'hidden'
        }}>
          <div style={{
            position: 'absolute',
            width: '500px',
            height: '500px',
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(124, 58, 237, 0.15) 0%, transparent 70%)',
            top: '-10%',
            left: '-5%',
            animation: 'float 20s ease-in-out infinite'
          }}></div>
          <div style={{
            position: 'absolute',
            width: '600px',
            height: '600px',
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(16, 185, 129, 0.1) 0%, transparent 70%)',
            bottom: '-15%',
            right: '-10%',
            animation: 'float 25s ease-in-out infinite reverse'
          }}></div>
        </div>

        {/* Personal Details Section with Photo */}
        <section style={{
          padding: '80px 20px 40px',
          maxWidth: '1200px',
          margin: '0 auto',
          position: 'relative',
          zIndex: 2
        }}>
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
              gap: '40px',
              alignItems: 'center',
              background: 'rgba(30, 41, 59, 0.4)',
              backdropFilter: 'blur(10px)',
              padding: '40px',
              borderRadius: '20px',
              border: '1px solid rgba(124, 58, 237, 0.2)',
              boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.37)'
            }}
          >
            {/* Left Column - Photo */}
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center'
            }}>
              <div style={{
                width: '250px',
                height: '250px',
                borderRadius: '20px',
                overflow: 'hidden',
                border: '4px solid',
                borderImage: 'linear-gradient(135deg, #0EA5E9 0%, #1E3A5F 100%) 1',
                boxShadow: '0 10px 40px rgba(102, 126, 234, 0.4)',
                position: 'relative'
              }}>
                <img 
                  src="/images/full-stack-developer.png" 
                  alt="Full Stack Expert"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover'
                  }}
                  onError={(e) => {
                    e.currentTarget.src = 'https://ui-avatars.com/api/?name=Vishnu+Charan&size=250&background=667eea&color=fff&font-size=0.4&bold=true';
                  }}
                />
              </div>
            </div>

            {/* Right Column - Personal Details */}
            <div>
              <h1 style={{
                fontSize: '2.5rem',
                fontWeight: 'bold',
                marginBottom: '0.5rem',
                background: 'linear-gradient(135deg, #0EA5E9 0%, #1E3A5F 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}>
                {personalInfo.name}
              </h1>
              <p style={{
                fontSize: '1.3rem',
                color: '#A78BFA',
                marginBottom: '1.5rem',
                fontWeight: '500'
              }}>
                {personalInfo.title}
              </p>

              {/* Contact Information Grid */}
              <div style={{
                display: 'grid',
                gap: '12px',
                fontSize: '1rem'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <FaMapMarkerAlt style={{ color: '#0EA5E9', fontSize: '1.2rem' }} />
                  <span style={{ color: '#E5E7EB' }}>{personalInfo.location}</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <FaEnvelope style={{ color: '#0EA5E9', fontSize: '1.2rem' }} />
                  <a href={`mailto:${personalInfo.email}`} style={{ color: '#E5E7EB', textDecoration: 'none' }}>
                    {personalInfo.email}
                  </a>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <FaWhatsapp style={{ color: '#0EA5E9', fontSize: '1.2rem' }} />
                  <a href={`tel:${personalInfo.phone}`} style={{ color: '#E5E7EB', textDecoration: 'none' }}>
                    {personalInfo.phone}
                  </a>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <FaLinkedin style={{ color: '#0EA5E9', fontSize: '1.2rem' }} />
                  <a href={personalInfo.linkedin} target="_blank" rel="noopener noreferrer" style={{ color: '#E5E7EB', textDecoration: 'none' }}>
                    LinkedIn Profile
                  </a>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <FaGithub style={{ color: '#0EA5E9', fontSize: '1.2rem' }} />
                  <a href={personalInfo.github} target="_blank" rel="noopener noreferrer" style={{ color: '#E5E7EB', textDecoration: 'none' }}>
                    GitHub Profile
                  </a>
                </div>
              </div>

              {/* Quick Stats */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, 1fr)',
                gap: '20px',
                marginTop: '30px'
              }}>
                <div style={{
                  background: 'rgba(102, 126, 234, 0.1)',
                  padding: '15px',
                  borderRadius: '10px',
                  textAlign: 'center',
                  border: '1px solid rgba(102, 126, 234, 0.2)'
                }}>
                  <div style={{ fontSize: '1.8rem', fontWeight: 'bold', color: '#0EA5E9' }}>
                    {personalInfo.experience}
                  </div>
                  <div style={{ fontSize: '0.9rem', color: '#9CA3AF', marginTop: '5px' }}>
                    Experience
                  </div>
                </div>
                <div style={{
                  background: 'rgba(102, 126, 234, 0.1)',
                  padding: '15px',
                  borderRadius: '10px',
                  textAlign: 'center',
                  border: '1px solid rgba(102, 126, 234, 0.2)'
                }}>
                  <div style={{ fontSize: '1.8rem', fontWeight: 'bold', color: '#0EA5E9' }}>
                    {personalInfo.projects}
                  </div>
                  <div style={{ fontSize: '0.9rem', color: '#9CA3AF', marginTop: '5px' }}>
                    Projects
                  </div>
                </div>
                <div style={{
                  background: 'rgba(102, 126, 234, 0.1)',
                  padding: '15px',
                  borderRadius: '10px',
                  textAlign: 'center',
                  border: '1px solid rgba(102, 126, 234, 0.2)'
                }}>
                  <div style={{ fontSize: '1.8rem', fontWeight: 'bold', color: '#0EA5E9' }}>
                    {personalInfo.clients}
                  </div>
                  <div style={{ fontSize: '0.9rem', color: '#9CA3AF', marginTop: '5px' }}>
                    Satisfaction
                  </div>
                </div>
                <div style={{
                  background: 'rgba(102, 126, 234, 0.1)',
                  padding: '15px',
                  borderRadius: '10px',
                  textAlign: 'center',
                  border: '1px solid rgba(102, 126, 234, 0.2)'
                }}>
                  <div style={{ fontSize: '1.8rem', fontWeight: 'bold', color: '#0EA5E9' }}>
                    {personalInfo.rating}
                  </div>
                  <div style={{ fontSize: '0.9rem', color: '#9CA3AF', marginTop: '5px' }}>
                    Rating
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </section>

        {/* Professional Summary Section */}
        <section style={{
          padding: '80px 20px',
          maxWidth: '1200px',
          margin: '0 auto',
          position: 'relative',
          zIndex: 2
        }}>
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 style={{
              fontSize: '2.5rem',
              fontWeight: 'bold',
              textAlign: 'center',
              marginBottom: '1rem',
              background: 'linear-gradient(135deg, #0EA5E9 0%, #1E3A5F 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text'
            }}>
              Professional Summary
            </h2>
            <p style={{
              fontSize: '1.1rem',
              lineHeight: '1.8',
              color: '#E5E7EB',
              textAlign: 'center',
              maxWidth: '900px',
              margin: '0 auto 3rem',
              padding: '0 20px'
            }}>
              Results-driven Full Stack Expert with 8+ years of experience building scalable web and mobile applications. 
              Proven track record of delivering 150+ successful projects with 95% client satisfaction. Expert in React, Node.js, 
              TypeScript, and cloud technologies. Passionate about creating innovative solutions that drive business growth and 
              enhance user experience. Strong problem-solver with excellent communication skills and a commitment to continuous learning.
            </p>
          </motion.div>

          {/* Technical Skills Grid */}
          <div style={{ marginTop: '60px' }}>
            <h3 style={{
              fontSize: '2rem',
              fontWeight: 'bold',
              textAlign: 'center',
              marginBottom: '2.5rem',
              color: '#F3F4F6'
            }}>
              Technical Skills
            </h3>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
              gap: '2rem',
              padding: '0 20px'
            }}>
              {technicalSkills.map((category, idx) => (
                <motion.div
                  key={category.category}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.1, duration: 0.5 }}
                  viewport={{ once: true }}
                  style={{
                    background: 'rgba(255, 255, 255, 0.05)',
                    backdropFilter: 'blur(10px)',
                    borderRadius: '16px',
                    padding: '1.5rem',
                    border: '1px solid rgba(255, 255, 255, 0.1)'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1.5rem' }}>
                    <span style={{ fontSize: '1.5rem', color: '#7C3AED' }}>{category.icon}</span>
                    <h4 style={{ fontSize: '1.25rem', fontWeight: '600', color: '#F3F4F6' }}>{category.category}</h4>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                    {category.skills.map((skill) => (
                      <div key={skill.name}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <span style={{ fontSize: '1.1rem', color: skill.color }}>{skill.icon}</span>
                            <span style={{ color: '#E5E7EB', fontSize: '0.95rem' }}>{skill.name}</span>
                          </div>
                          <span style={{ color: '#9CA3AF', fontSize: '0.85rem' }}>{skill.level}%</span>
                        </div>
                        <div style={{
                          width: '100%',
                          height: '6px',
                          backgroundColor: 'rgba(255, 255, 255, 0.1)',
                          borderRadius: '10px',
                          overflow: 'hidden'
                        }}>
                          <motion.div
                            initial={{ width: 0 }}
                            whileInView={{ width: `${skill.level}%` }}
                            transition={{ duration: 1, delay: 0.2 }}
                            viewport={{ once: true }}
                            style={{
                              height: '100%',
                              background: `linear-gradient(90deg, ${skill.color}, ${skill.color}dd)`,
                              borderRadius: '10px'
                            }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Professional Experience */}
          <div style={{ marginTop: '80px' }}>
            <h3 style={{
              fontSize: '2rem',
              fontWeight: 'bold',
              textAlign: 'center',
              marginBottom: '2.5rem',
              color: '#F3F4F6'
            }}>
              Professional Experience
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem', padding: '0 20px' }}>
              {experience.map((job, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, x: -50 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ delay: idx * 0.15, duration: 0.5 }}
                  viewport={{ once: true }}
                  style={{
                    background: 'rgba(255, 255, 255, 0.05)',
                    backdropFilter: 'blur(10px)',
                    borderRadius: '16px',
                    padding: '2rem',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    borderLeft: '4px solid #7C3AED'
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem', flexWrap: 'wrap', gap: '1rem' }}>
                    <div>
                      <h4 style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#F3F4F6', marginBottom: '0.25rem' }}>
                        {job.title}
                      </h4>
                      <p style={{ fontSize: '1.1rem', color: '#7C3AED', fontWeight: '500' }}>{job.company}</p>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ color: '#9CA3AF', fontSize: '0.95rem', display: 'flex', alignItems: 'center', gap: '0.5rem', justifyContent: 'flex-end' }}>
                        <FaCalendar />
                        {job.period}
                      </div>
                      <div style={{ color: '#9CA3AF', fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.25rem', justifyContent: 'flex-end' }}>
                        <FaMapMarkerAlt />
                        {job.location}
                      </div>
                    </div>
                  </div>
                  <p style={{ color: '#E5E7EB', fontSize: '1rem', lineHeight: '1.6', marginBottom: '1.5rem' }}>
                    {job.description}
                  </p>
                  <div style={{ marginBottom: '1.5rem' }}>
                    <h5 style={{ color: '#F3F4F6', fontSize: '1rem', fontWeight: '600', marginBottom: '0.75rem' }}>
                      Key Achievements:
                    </h5>
                    <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                      {job.achievements.map((achievement, i) => (
                        <li key={i} style={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: '0.75rem',
                          marginBottom: '0.5rem',
                          color: '#E5E7EB',
                          fontSize: '0.95rem',
                          lineHeight: '1.6'
                        }}>
                          <FaCheckCircle style={{ color: '#10B981', fontSize: '1rem', marginTop: '0.25rem', flexShrink: 0 }} />
                          {achievement}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                    {job.technologies.map((tech) => (
                      <span key={tech} style={{
                        padding: '0.4rem 0.9rem',
                        backgroundColor: 'rgba(124, 58, 237, 0.2)',
                        color: '#C4B5FD',
                        borderRadius: '20px',
                        fontSize: '0.85rem',
                        fontWeight: '500',
                        border: '1px solid rgba(124, 58, 237, 0.3)'
                      }}>
                        {tech}
                      </span>
                    ))}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Education */}
          <div style={{ marginTop: '80px' }}>
            <h3 style={{
              fontSize: '2rem',
              fontWeight: 'bold',
              textAlign: 'center',
              marginBottom: '2.5rem',
              color: '#F3F4F6'
            }}>
              Education
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem', padding: '0 20px' }}>
              {education.map((edu, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2, duration: 0.5 }}
                  viewport={{ once: true }}
                  style={{
                    background: 'rgba(255, 255, 255, 0.05)',
                    backdropFilter: 'blur(10px)',
                    borderRadius: '16px',
                    padding: '2rem',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    borderLeft: '4px solid #10B981'
                  }}
                >
                  <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start', marginBottom: '1rem' }}>
                    <FaGraduationCap style={{ fontSize: '2rem', color: '#10B981', flexShrink: 0 }} />
                    <div style={{ flex: 1 }}>
                      <h4 style={{ fontSize: '1.4rem', fontWeight: 'bold', color: '#F3F4F6', marginBottom: '0.5rem' }}>
                        {edu.degree}
                      </h4>
                      <p style={{ fontSize: '1.1rem', color: '#10B981', fontWeight: '500', marginBottom: '0.25rem' }}>
                        {edu.institution}
                      </p>
                      <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap', color: '#9CA3AF', fontSize: '0.9rem', marginTop: '0.5rem' }}>
                        <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <FaMapMarkerAlt /> {edu.location}
                        </span>
                        <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <FaCalendar /> {edu.period}
                        </span>
                        <span style={{ fontWeight: '500', color: '#10B981' }}>
                          Grade: {edu.grade}
                        </span>
                      </div>
                    </div>
                  </div>
                  <ul style={{ listStyle: 'none', padding: 0, margin: '1rem 0 0 0' }}>
                    {edu.achievements.map((achievement, i) => (
                      <li key={i} style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: '0.75rem',
                        marginBottom: '0.5rem',
                        color: '#E5E7EB',
                        fontSize: '0.95rem',
                        lineHeight: '1.6'
                      }}>
                        <FaCheckCircle style={{ color: '#10B981', fontSize: '1rem', marginTop: '0.25rem', flexShrink: 0 }} />
                        {achievement}
                      </li>
                    ))}
                  </ul>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Certifications */}
          <div style={{ marginTop: '80px' }}>
            <h3 style={{
              fontSize: '2rem',
              fontWeight: 'bold',
              textAlign: 'center',
              marginBottom: '2.5rem',
              color: '#F3F4F6'
            }}>
              Certifications
            </h3>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
              gap: '1.5rem',
              padding: '0 20px'
            }}>
              {certifications.map((cert, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ delay: idx * 0.1, duration: 0.4 }}
                  viewport={{ once: true }}
                  whileHover={{ y: -5, boxShadow: '0 10px 30px rgba(124, 58, 237, 0.3)' }}
                  style={{
                    background: 'rgba(255, 255, 255, 0.05)',
                    backdropFilter: 'blur(10px)',
                    borderRadius: '12px',
                    padding: '1.5rem',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    display: 'flex',
                    gap: '1rem',
                    alignItems: 'flex-start'
                  }}
                >
                  <FaCertificate style={{ fontSize: '1.8rem', color: '#F59E0B', flexShrink: 0 }} />
                  <div>
                    <h4 style={{ fontSize: '1.1rem', fontWeight: '600', color: '#F3F4F6', marginBottom: '0.5rem' }}>
                      {cert.name}
                    </h4>
                    <p style={{ color: '#9CA3AF', fontSize: '0.9rem', marginBottom: '0.25rem' }}>
                      {cert.issuer}
                    </p>
                    <span style={{ color: '#7C3AED', fontSize: '0.85rem', fontWeight: '500' }}>
                      {cert.year}
                    </span>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Achievements */}
          <div style={{ marginTop: '80px' }}>
            <h3 style={{
              fontSize: '2rem',
              fontWeight: 'bold',
              textAlign: 'center',
              marginBottom: '2.5rem',
              color: '#F3F4F6'
            }}>
              Awards & Recognition
            </h3>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
              gap: '1.5rem',
              padding: '0 20px'
            }}>
              {achievements.map((achievement, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.1, duration: 0.5 }}
                  viewport={{ once: true }}
                  whileHover={{ y: -8, boxShadow: '0 15px 40px rgba(239, 68, 68, 0.3)' }}
                  style={{
                    background: 'rgba(255, 255, 255, 0.05)',
                    backdropFilter: 'blur(10px)',
                    borderRadius: '16px',
                    padding: '1.8rem',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    textAlign: 'center'
                  }}
                >
                  <div style={{
                    fontSize: '3rem',
                    color: '#EF4444',
                    marginBottom: '1rem',
                    display: 'flex',
                    justifyContent: 'center'
                  }}>
                    {achievement.icon}
                  </div>
                  <h4 style={{ fontSize: '1.2rem', fontWeight: '600', color: '#F3F4F6', marginBottom: '0.5rem' }}>
                    {achievement.title}
                  </h4>
                  <p style={{ color: '#9CA3AF', fontSize: '0.9rem', lineHeight: '1.5' }}>
                    {achievement.description}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>

          {/* CTA Section */}
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            style={{
              marginTop: '80px',
              padding: '3rem 2rem',
              background: 'linear-gradient(135deg, rgba(124, 58, 237, 0.2) 0%, rgba(16, 185, 129, 0.2) 100%)',
              backdropFilter: 'blur(10px)',
              borderRadius: '20px',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              textAlign: 'center'
            }}
          >
            <h3 style={{
              fontSize: '2rem',
              fontWeight: 'bold',
              color: '#F3F4F6',
              marginBottom: '1rem'
            }}>
              Let's Work Together
            </h3>
            <p style={{
              fontSize: '1.1rem',
              color: '#E5E7EB',
              marginBottom: '2rem',
              maxWidth: '700px',
              margin: '0 auto 2rem'
            }}>
              I'm currently available for freelance projects and full-time opportunities. 
              Let's discuss how I can help bring your ideas to life.
            </p>
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
              <a 
                href="/contact"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  padding: '1rem 2rem',
                  backgroundColor: '#7C3AED',
                  color: 'white',
                  borderRadius: '50px',
                  textDecoration: 'none',
                  fontWeight: '600',
                  fontSize: '1.05rem',
                  transition: 'all 0.3s ease',
                  border: '2px solid transparent'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#6D28D9';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 10px 30px rgba(124, 58, 237, 0.5)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#7C3AED';
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                <FaRocket />
                Get In Touch
              </a>
              <a 
                href="/resume.pdf"
                download
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  padding: '1rem 2rem',
                  backgroundColor: 'transparent',
                  color: 'white',
                  borderRadius: '50px',
                  textDecoration: 'none',
                  fontWeight: '600',
                  fontSize: '1.05rem',
                  transition: 'all 0.3s ease',
                  border: '2px solid rgba(255, 255, 255, 0.3)'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
                  e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.5)';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.3)';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                <FaDownload />
                Download Full Resume
              </a>
            </div>
          </motion.div>
        </section>
      </div>
    </>
  );
};

export default Resume;

